import { BrowserModule } from '@angular/platform-browser';
import { ErrorHandler, NgModule } from '@angular/core';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ToastrModule } from 'ngx-toastr';
import { OverlayModule } from '@angular/cdk/overlay';
import { NgClickOutsideModule } from 'ng-click-outside2';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatRadioModule } from '@angular/material/radio';
import { NgxSpinnerModule } from 'ngx-spinner';
import { MatStepperModule } from '@angular/material/stepper';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ApmErrorHandler, ApmModule, ApmService } from '@elastic/apm-rum-angular';
import { firstValueFrom } from 'rxjs';
import { AppComponent } from './app.component';
import { DataTypesModule } from './components/data-type-widgets/data-type/data-types.module';
import { DataTypeService } from './components/data-type-widgets/data-type/services/data-type.service';
import { SystemService } from './system/system.service';
import { AppRoutingModule } from './app-routing-module';
import { MaintainTableComponent } from './components/views/maintain/maintain-table/maintain-table.component';
import { MaintainService } from './components/views/maintain/maintain.service';
import { EncodingInterceptor } from './common/interceptors/encoding-interceptor';
import { SignInComponent } from './components/views/sign-in/sign-in.component';
import { AuthInterceptor } from './common/interceptors/auth-interceptor';
import { SignInService } from './common/services/sign-in/sign-in.service';
import { EnvironmentService } from './common/services/env-configuration/environment.service';
import { NotFoundComponent } from './components/views/not-found/not-found.component';
import { MaintainFormComponent } from './components/views/maintain/maintain-form/maintain-form.component';
import { CacheInterceptor } from './common/interceptors/cache-interceptor';
import { EntityGroupDetailsComponent } from './components/views/entity-group/entity-group-details/entity-group-details.component';
import { EntityGroupDetailsService } from './components/views/entity-group/entity-group-details/entity-group-details.service';
import { LookupService } from './components/data-type-widgets/data-type/shared/lookup/lookup.service';
import { AdminBaseModule } from './common/admin-base/admin-base.module';
import { RowActionsService } from './components/views/maintain/maintain-table/maintain-table-row-actions/row-actions.service';
import { EntityGroupHeaderComponent } from './components/views/entity-group/entity-group-details/entity-group-header/entity-group-header.component';
import { DrawLottolandComplianceService } from './components/dialogs/draw-lottoland-compliance/draw-lottoland-compliance.service';
import { ConfirmationModalComponent } from './components/dialogs/confirmation-modal/confirmation-modal.component';
import { ExpandedRowComponent } from './components/views/maintain/maintain-table/expanded-row/expanded-row.component';
import { MaintainTableTabsComponent } from './components/views/maintain/maintain-table/maintain-table-tabs/maintain-table-tabs.component';
import { LmTitleService } from './common/services/title.service';
import { DataSourceFactory } from './components/views/maintain/data-source/data-source-factory';
import { DataSourceSwitcherComponent } from './components/views/maintain/maintain-table/data-source-switcher/data-source-switcher.component';
import { DataTypeComparatorsService } from './components/data-type-widgets/data-type/services/data-type-comparators.service';
import { ThemeService } from './common/services/theme.service';
import { ResultsInformationComponent } from './components/views/maintain/maintain-table/results-information/results-information.component';
import { DataTypeAggsService } from './components/data-type-widgets/data-type/services/data-type-aggs.service';
import { MainTableV2Component } from './components/views/main-table-v2/main-table-v2.component';
import { TriValueCheckboxComponent } from './components/views/main-table-v2/components/tri-value-checkbox/tri-value-checkbox.component';
import { TableFilterV2Component } from './components/views/main-table-v2/components/table-filter-v2/table-filter-v2.component';
import { TableActionsBarV2Component } from './components/views/main-table-v2/components/table-actions-bar-v2/table-actions-bar-v2.component';
import { MainTableContentV2Component } from './components/views/main-table-v2/components/main-table-content-v2/main-table-content-v2.component';
import { MainTableContentV2RowActionsComponent } from './components/views/main-table-v2/components/main-table-content-v2/main-table-content-v2-row-actions/main-table-content-v2-row-actions.component';
import { MainTableV2TabsComponent } from './components/views/main-table-v2/components/main-table-v2-tabs/main-table-v2-tabs.component';
import { MainTableContentV2LoadingComponent } from './components/views/main-table-v2/components/main-table-content-v2/main-table-content-v2-loading/main-table-content-v2-loading.component';
import { MainTableV2PaginatorDecoratorComponent } from './components/views/main-table-v2/components/main-table-v2-paginator-decorator/main-table-v2-paginator-decorator.component';
import { MainTableV2AggsComponent } from './components/views/main-table-v2/components/main-table-v2-aggs/main-table-v2-aggs.component';
import { AdminContextService } from './common/services/admin.context.service';
import { AdminContextInterceptor } from './common/interceptors/admin-context-interceptor';
import { QueryDataSource } from './components/views/maintain/data-source/query-data-source';
import { CurrencyService } from './common/services/currency.service';
import { EntityGroupActionsComponent } from './components/views/entity-group/entity-group-details/entity-group-actions/entity-group-actions.component';
import { ColumnFilterComponent } from './components/views/maintain/maintain-table/column-filter/column-filter.component';
import { AutoWithdrawalsConfigComponent } from './components/views/custom-views/auto-withdrawals-config/auto-withdrawals-config.component';
import { AutoWithdrawalsConfigService } from './components/views/custom-views/auto-withdrawals-config/auto-withdrawals-config.service';
import { AutoWithdrawalsTierComponent } from './components/views/custom-views/auto-withdrawals-config/auto-withdrawals-tier/auto-withdrawals-tier.component';
import { DepositTickeActionRulesComponent } from './components/views/custom-views/deposit-ticke-action-rules/deposit-ticke-action-rules.component';
import { DepositTickerSingleRuleComponent } from './components/views/custom-views/deposit-ticke-action-rules/deposit-ticker-single-rule/deposit-ticker-single-rule.component';
import { AppTableIndividualInformationsComponent } from './components/views/main-table-v2/app-table-individual-informations/app-table-individual-informations.component';
import { AppWithdrawTableIndividualInfoComponent } from './components/views/main-table-v2/app-table-individual-informations/app-withdraw-table-individual-info/app-withdraw-table-individual-info.component';
import { AppManualAdjustmentModalComponent } from './components/views/entity-group/entity-group-details/entity-group-actions/app-wallet-manual-adjustment/app-manual-adjustment-modal/app-manual-adjustment-modal.component';
import { ManualAdjustmentCashComponent } from './components/views/entity-group/entity-group-details/entity-group-actions/app-wallet-manual-adjustment/app-manual-adjustment-modal/manual-adjustment-cash/manual-adjustment-cash.component';
import { ManualAdjustmentDiamondsComponent } from './components/views/entity-group/entity-group-details/entity-group-actions/app-wallet-manual-adjustment/app-manual-adjustment-modal/manual-adjustment-diamonds/manual-adjustment-diamonds.component';
import { ManualAdjustmentConfirmationComponent } from './components/views/entity-group/entity-group-details/entity-group-actions/app-wallet-manual-adjustment/app-manual-adjustment-modal/manual-adjustement-confirmation/manual-adjustment-confirmation.component';
import { DiamondsComponent } from './components/views/entity-group/entity-group-details/entity-group-actions/app-wallet-manual-adjustment/app-manual-adjustment-modal/diamonds/diamonds.component';
import { SimpleMoneyComponent } from './components/views/entity-group/entity-group-details/entity-group-actions/app-wallet-manual-adjustment/app-manual-adjustment-modal/simple-money/simple-money.component';
import { ConfigService } from './shared/config.service';
import { FirebaseModule } from './shared/firebase/firebase.module';
import { VERSION } from './version';
import { SharedModule } from './shared/shared.module';
import { IosNativeSupportSyncComponent } from './components/views/entity-group/entity-group-details/entity-group-actions/ios-native-support-sync/ios-native-support-sync.component';
import { IosNativeSupportSyncModalComponent } from './components/views/entity-group/entity-group-details/entity-group-actions/ios-native-support-sync/ios-native-support-sync-modal/ios-native-support-sync-modal.component';
import { ResolveGameLineActionModalComponent } from './components/action/resolve-game-line-action-modal/resolve-game-line-action-modal.component';
import { BinRangeExclusionComponent } from './components/views/custom-views/auto-withdrawals-config/bin-range-exclusion/bin-range-exclusion.component';
import { PlayerMaintainTableComponent } from './components/views/maintain/player-maintain-table/player-maintain-table.component';
import { PlayerEntityGroupSubsetComponent } from './components/views/entity-group/entity-group-details/player-entity-group-subset/player-entity-group-subset.component';
import { PlayerLimitsService } from './feature/manage/player/player-details/views/player-view/player-limits-view/player-limits.service';
import { HttpLoggingInterceptor } from './shared/http-logging-interceptor';
import { providePromotionFeatureFlags } from './core/feature-flags/promotion-feature-flags.provider';
import { ConfirmationReasonModalComponent } from './components/dialogs/confirmation-reason-modal/confirmation-reason-modal.component';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { CountriesService } from './shared/services/countries.service';
import { RadioButtonWithDescriptionComponent } from './components/radio-button-with-description/radio-button-with-description';

@NgModule({
  declarations: [
    AppComponent,
    MaintainTableComponent,
    MaintainTableTabsComponent,
    SignInComponent,
    NotFoundComponent,
    MaintainFormComponent,
    EntityGroupDetailsComponent,
    EntityGroupHeaderComponent,
    ConfirmationModalComponent,
    ConfirmationReasonModalComponent,
    ExpandedRowComponent,
    DataSourceSwitcherComponent,
    ResultsInformationComponent,
    MainTableV2Component,
    TriValueCheckboxComponent,
    TableFilterV2Component,
    TableActionsBarV2Component,
    MainTableContentV2Component,
    MainTableContentV2RowActionsComponent,
    MainTableV2TabsComponent,
    MainTableContentV2LoadingComponent,
    MainTableV2PaginatorDecoratorComponent,
    MainTableV2AggsComponent,
    EntityGroupActionsComponent,
    ColumnFilterComponent,
    AutoWithdrawalsConfigComponent,
    AutoWithdrawalsTierComponent,
    DepositTickeActionRulesComponent,
    DepositTickerSingleRuleComponent,
    AppTableIndividualInformationsComponent,
    AppWithdrawTableIndividualInfoComponent,
    AppManualAdjustmentModalComponent,
    ManualAdjustmentCashComponent,
    ManualAdjustmentDiamondsComponent,
    ManualAdjustmentConfirmationComponent,
    DiamondsComponent,
    SimpleMoneyComponent,
    IosNativeSupportSyncModalComponent,
    IosNativeSupportSyncComponent,
    ResolveGameLineActionModalComponent,
    BinRangeExclusionComponent,
    PlayerMaintainTableComponent,
    PlayerEntityGroupSubsetComponent,
  ],
  bootstrap: [AppComponent],
  exports: [AppTableIndividualInformationsComponent],
  imports: [
    BrowserModule,
    FirebaseModule,
    ApmModule,
    FormsModule,
    ReactiveFormsModule,
    BrowserAnimationsModule,
    DataTypesModule,
    AppRoutingModule,
    AdminBaseModule,
    NgClickOutsideModule,
    ToastrModule.forRoot({
      onActivateTick: true,
      positionClass: 'toast-bottom-right',
      maxOpened: 6,
      timeOut: 3000,
    }),
    OverlayModule,
    MatExpansionModule,
    MatRadioModule,
    MatInputModule,
    MatFormFieldModule,
    NgxSpinnerModule,
    MatStepperModule,
    MatButtonToggleModule,
    MatIconModule,
    MatProgressSpinnerModule,
    SharedModule,
    RadioButtonWithDescriptionComponent,
  ],
  providers: [
    {
      provide: ErrorHandler,
      useClass: ApmErrorHandler,
    },
    ConfigService,
    ApmService,
    ThemeService,
    CurrencyService,
    AdminContextService,
    DataSourceFactory,
    EnvironmentService,
    DataTypeService,
    DataTypeComparatorsService,
    DataTypeAggsService,
    QueryDataSource,
    SystemService,
    MaintainService,
    SignInService,
    LmTitleService,
    EntityGroupDetailsService,
    LookupService,
    AutoWithdrawalsConfigService,
    CountriesService,
    RowActionsService,
    DrawLottolandComplianceService,
    PlayerLimitsService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpLoggingInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CacheInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: EncodingInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AdminContextInterceptor,
      multi: true,
    },
    providePromotionFeatureFlags(),
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class AppModule {
  constructor(configService: ConfigService, apmService: ApmService) {
    this.initialiseApm(configService, apmService);
  }

  private async initialiseApm(configService: ConfigService, apmService: ApmService): Promise<void> {
    try {
      const config = await firstValueFrom(configService.getConfig());

      if (config.apm) {
        apmService.init({
          serviceName: 'admin-ui',
          serviceVersion: VERSION,
          serverUrl: '/apm',
          active: config.apm.active,
          transactionSampleRate: config.apm.transactionSampleRate,
          flushInterval: config.apm.flushInterval,
          eventsLimit: config.apm.eventsLimit,
          apiVersion: 3,
          breakdownMetrics: true,
          propagateTracestate: true,
        });
      }
    } catch (error) {
      console.error('Unable to initialise APM. Error reading config.', error);
    }
  }
}
